import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BellIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserPlusIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { translateNotification } from '../../utils/notifications';
import toast from 'react-hot-toast';
import EmptyState from '../ui/EmptyState';
import Skeleton from 'react-loading-skeleton';
import { skeletonProps } from '../../styles/skeletonTheme';

function getIconAndColors(type) {
  const iconMap = {
    success: {
      icon: CheckCircleIcon,
      color: 'text-success-600 ',
      bgColor: 'bg-success-100 ',
    },
    info: {
      icon: UserPlusIcon,
      color: 'text-orange-600 ',
      bgColor: 'bg-orange-100 ',
    },
    warning: {
      icon: ExclamationTriangleIcon,
      color: 'text-yellow-600 ',
      bgColor: 'bg-yellow-100 ',
    },
    error: {
      icon: ExclamationTriangleIcon,
      color: 'text-red-600 ',
      bgColor: 'bg-red-100 ',
    },
  };
  return iconMap[type] || iconMap.info;
}

function NotificationsCenter({ isOpen, onClose, onNotificationUpdate, onNavigate }) {
  const { t, formatMessage } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/notifications', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();
      const formattedNotifications = data.notifications.map((notification) => ({
        ...notification,
        time: notification.time_ago,
        ...getIconAndColors(notification.type),
      }));

      setNotifications(formattedNotifications);

      if (onNotificationUpdate) {
        const unread = formattedNotifications.filter((n) => !n.read).length;
        onNotificationUpdate(unread);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error(t.notifications.failedToLoad);
    } finally {
      setLoading(false);
    }
  }, [onNotificationUpdate, t.notifications.failedToLoad]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const filteredNotifications = notifications.filter((notification) => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification.read;
    return notification.type === filter;
  });

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = async (id) => {
    try {
      const response = await fetch(`/api/notifications/${id}/read`, {
        method: 'PUT',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }

      setNotifications((prev) => {
        const next = prev.map((notification) =>
          notification.id === id ? { ...notification, read: true } : notification
        );
        if (onNotificationUpdate) {
          onNotificationUpdate(next.filter((n) => !n.read).length);
        }
        return next;
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error(t.notifications.failedToUpdate);
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await fetch('/api/notifications/mark-all-read', {
        method: 'PUT',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read');
      }

      setNotifications((prev) => prev.map((notification) => ({ ...notification, read: true })));
      toast.success(t.notifications.allMarkedRead);

      if (onNotificationUpdate) {
        onNotificationUpdate(0);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error(t.notifications.failedToUpdateAll);
    }
  };

  const deleteNotification = async (id) => {
    try {
      const response = await fetch(`/api/notifications/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete notification');
      }

      setNotifications((prev) => {
        const next = prev.filter((notification) => notification.id !== id);
        if (onNotificationUpdate) {
          onNotificationUpdate(next.filter((n) => !n.read).length);
        }
        return next;
      });

      toast.success(t.notifications.deleted);
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error(t.notifications.failedToDelete);
    }
  };

  const openNotification = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    if (notification.action_path && onNavigate) {
      onNavigate(notification.action_path);
      onClose();
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          key="notifications-panel"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[10000] overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-label={t.notifications.title}
        >
          <motion.button
            type="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 cursor-default bg-secondary-900/50 backdrop-blur-[2px] "
            onClick={onClose}
            aria-label={t.common?.close || 'Close'}
          />

          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            className="notifications-panel safe-top safe-bottom absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-secondary-200/80 bg-white shadow-elegant  "
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-secondary-200/80 bg-white p-6   max-lg:p-4">
              <div className="flex items-center space-x-3">
                <div className="icon-tile h-10 w-10 from-orange-500 to-amber-500">
                  <BellIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-secondary-900 ">{t.notifications.title}</h2>
                  <p className="text-sm text-secondary-500 ">
                    {unreadCount} {t.notifications.unread}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="touch-target rounded-xl p-2 text-secondary-400 transition-[color,background-color] hover:bg-secondary-100 hover:text-secondary-700   "
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="border-b border-secondary-200/80 bg-white p-4  ">
              <div className="max-lg:mobile-scroll-x lg:flex lg:flex-wrap lg:gap-2 lg:overflow-visible lg:pb-0">
                {[
                  { key: 'all', label: t.notifications.all },
                  { key: 'unread', label: t.notifications.unread },
                  { key: 'success', label: t.notifications.success },
                  { key: 'info', label: t.notifications.info },
                ].map((filterOption) => (
                  <button
                    key={filterOption.key}
                    type="button"
                    onClick={() => setFilter(filterOption.key)}
                    className={`filter-chip shrink-0 ${
                      filter === filterOption.key ? 'filter-chip-active' : 'filter-chip-inactive'
                    }`}
                  >
                    {filterOption.label}
                  </button>
                ))}
              </div>
              {unreadCount > 0 ? (
                <button
                  type="button"
                  onClick={markAllAsRead}
                  className="mt-3 text-sm font-medium text-orange-600 hover:text-orange-700"
                >
                  {t.notifications.markAllRead}
                </button>
              ) : null}
            </div>

            <div className="flex-1 overflow-y-auto bg-white ">
              {loading ? (
                <div className="space-y-3 p-4">
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <div
                      key={`notif-skel-${idx}`}
                      className="flex gap-3 rounded-2xl border border-secondary-100 p-4 "
                    >
                      <Skeleton height={40} width={40} borderRadius={12} {...skeletonProps} />
                      <div className="flex-1 space-y-2">
                        <Skeleton height={12} width="75%" {...skeletonProps} />
                        <Skeleton height={10} {...skeletonProps} />
                        <Skeleton height={10} width="33%" {...skeletonProps} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredNotifications.length === 0 ? (
                <EmptyState
                  icon={BellIcon}
                  title={t.notifications.noNotifications}
                  description={t.notifications.noNotificationsDescription}
                  compact
                  className="mx-4 my-8"
                />
              ) : (
                <AnimatePresence mode="popLayout">
                  {filteredNotifications.map((notification) => {
                    const Icon = notification.icon;
                    const isNavigable = Boolean(notification.action_path && onNavigate);
                    const { title, message } = translateNotification(notification, t, formatMessage);
                    return (
                      <motion.div
                        key={notification.id}
                        layout
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className={`notification-item ${!notification.read ? 'notification-item-unread' : ''} ${
                          isNavigable ? 'cursor-pointer transition-colors hover:bg-orange-50/60 ' : ''
                        }`}
                        role={isNavigable ? 'button' : undefined}
                        tabIndex={isNavigable ? 0 : undefined}
                        onClick={isNavigable ? () => void openNotification(notification) : undefined}
                        onKeyDown={
                          isNavigable
                            ? (event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  void openNotification(notification);
                                }
                              }
                            : undefined
                        }
                      >
                        <div className="flex items-start space-x-3">
                          <div
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ring-white/40 ${notification.bgColor}`}
                          >
                            <Icon className={`h-5 w-5 ${notification.color}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p
                                className={`text-sm ${
                                  !notification.read
                                    ? 'font-semibold text-secondary-900 '
                                    : 'font-medium text-secondary-700 '
                                }`}
                              >
                                {title}
                              </p>
                              <div className="flex shrink-0 items-center space-x-2">
                                {isNavigable ? (
                                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-orange-500" aria-hidden />
                                ) : null}
                                <span className="text-xs text-secondary-500 ">
                                  {notification.time}
                                </span>
                                {!notification.read ? (
                                  <div className="h-2 w-2 rounded-full bg-orange-500" />
                                ) : null}
                              </div>
                            </div>
                            <p className="mt-1 text-sm text-secondary-600 ">
                              {message}
                            </p>
                            <div className="mt-2 flex items-center space-x-2">
                              {!notification.read ? (
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void markAsRead(notification.id);
                                  }}
                                  className="text-xs font-medium text-orange-600 hover:text-orange-700"
                                >
                                  {t.notifications.markAsRead}
                                </button>
                              ) : null}
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void deleteNotification(notification.id);
                                }}
                                className="text-xs font-medium text-red-600 hover:text-red-700"
                              >
                                {t.notifications.delete}
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              )}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
}

export default NotificationsCenter;
